import React, { useContext, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AgGridService from "../../../../shared/services/ag-grid/ag-grid.service";
import ReportListService from "./report-list.service";
import ReportListToolbarComponent from "./components/report-list-toolbar/report-list-toolbar.component";
import useReportingAndAnalyticsApi from "../useReportingAndAnalyticsApi";
import IException from "../../../../shared/contracts/IException";
import useLoader from "../../../../shared/components/loading-dialog/useLoader.hook";
import LoadingDialogComponent from "../../../../shared/components/loading-dialog/loading-dialog.component";
import CreateReportDialogComponent from "../create-report-dialog/create-report-dialog.component";
import useAppStoreContext from "../../../../shared/context/app-store/app-store.context";
import { AdvancedFilterOptionsContext } from "../../../../shared/context/advanced-filter-options/advanced-filter-options-context";
import IFilterOptionsProps from "../../../../shared/contracts/IFilterOptionsProps";
import WizardReportComponent from "../../../components/report-details-wizard/wizard-report.component";
import useRouteHelper from "../../../../shared/routes/useRouteHelper.hook";



function ReportListPage({ }: {}) {

  const { advancedFilterOptions, dispatch } = useContext(AdvancedFilterOptionsContext)!;
  const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);
  const { getReportList, getFilterOperatorsList } = useReportingAndAnalyticsApi();
  const { reportList, setReportList, } = useAppStoreContext();
  const [showCreateReportsDialog, setShowCreateReportsDialog] = useState<boolean>(false);
  const [isReloadData, setIsReloadData] = useState<boolean>(false);
  const { getDefaultColDef, getDefaultProps } = new AgGridService();
  const [showReportWizard, setShowReportWizard] = useState(false);


  const reportListLoader = useLoader({
    loaderName: "Report List",
    loadAsync: () =>
      getReportList({
        doIf: () => reportListLoader.isNotLoading,
        doBefore: () => {
          reportListLoader.SET_LOADING("Fetching...");
          setReportList([]);
        },
        onFulfilled: (data) => {
          setReportList(data);
          reportListLoader.SET_SUCCESS("Fetch Success!");
        },
        onException: (exception: IException) => {
          reportListLoader.SET_FAILURE(
            `${exception.title} - ${exception.message}`
          );
        },
      }),
  });

  const filterOperatorDataLoader = useLoader({
    loaderName: 'Load Filter Operators',
    loadAsync: () => getFilterOperatorsList(
      {
        doIf: () => filterOperatorDataLoader.isNotLoading, //TODO need an additional check || filterOperators.length === 0,
        doBefore: () => {
          filterOperatorDataLoader.SET_LOADING('Loading Filter Operators...')
        },
        onFulfilled: (data: IFilterOptionsProps) => {
          dispatch({
            type: 'UPDATE',
            payload: {
              filterOperators: data.options,
              stringFilters: data.stringOptions,
              dateFilters: data.dateOptions,
              numberFilters: data.numberOptions,
              filterOperatorValues: data.filterOperatorValues
            }
          });
          filterOperatorDataLoader.SET_SUCCESS('Success.!');
        },
        onException: filterOperatorDataLoader.SET_EXCEPTION
      })
  });

  useEffect(() => {
    // // Also read and set accessToken from local storage when component mounts
    // const token = localStorage.getItem("accessToken");
    // if (token) {
    //   cacheAccessToken(token);
    // }
    reportListLoader.loadAsync();
    filterOperatorDataLoader.loadAsync()
  }, [isReloadData]);

  const handleRowClick = (reportId: number) => {
    setHighlightedRowId(reportId);
  };

  const gridStyle = {
    height: `calc(100vh - 108px)`,
    padding: "1px 10px 10px",
    width: "auto", // Always use auto to handle dynamic width
  };

  const columnDefs = ReportListService.colDefs.map((colDef) => {
    if (colDef.field === "formattedModifiedDate") {
      return {
        ...colDef,
        filter: true,
        resizable: true,
        minWidth: 220,
        width: 220,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
        },
        cellRendererParams: {
          onRowClick: handleRowClick,
        },
      };
    } else {
      return {
        ...colDef,
        filter: true,
        resizable: true,
        minWidth: 100,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
        },
        cellRendererParams: {
          onRowClick: handleRowClick,
        },
      };
    }
  });

  const handleWizardClose = (reloadAll: boolean) => {
    if (reloadAll) {
      reportListLoader.loadAsync();
      filterOperatorDataLoader.loadAsync();
    }
    setShowReportWizard(false);
  }

  return (<>
    <LoadingDialogComponent loaderHook={reportListLoader} />
    <LoadingDialogComponent loaderHook={filterOperatorDataLoader} />
    <ReportListToolbarComponent
      onReload={reportListLoader.loadAsync}
      handleCreateReportClick={() => setShowCreateReportsDialog(true)}
      handleShowCreateReportWizard={() => setShowReportWizard(true)}
    />

    {/* Redesigned-Report Wizard */}
    <WizardReportComponent reportCode={null}
      open={showReportWizard} onClose={handleWizardClose} />

    {
      showCreateReportsDialog && (
        <CreateReportDialogComponent
          open={showCreateReportsDialog}
          onClose={() => setShowCreateReportsDialog(false)}
          onApply={() => {
            setShowCreateReportsDialog(false);
            setIsReloadData(true);
          }}
        />
      )
    }
    <div className={"ag-theme-quartz"} style={gridStyle}>
      <AgGridReact
        {...getDefaultProps({ rowHeight: 30, singleClickEdit: true })}
        onGridReady={(e) => {
          setTimeout(() => {
            e.api.autoSizeAllColumns();
          }, 0);
        }}
        defaultColDef={getDefaultColDef()}
        columnDefs={columnDefs}
        rowData={reportList || []}
        pagination={true}
        paginationPageSize={20}
      />
    </div>
  </>);
}

export default ReportListPage;
